import AbstractGroupItem from '../item/abstractGroupItem';

/**
 * Check if the given items build a chain by overlapping
 */
export default class ItemOverlappingCollisionChecker {
    /**
     * @type {TreeCollisionChecker}
     */
    treeCollisionChecker = null;

    /**
     * @param {TreeCollisionChecker} treeCollisionChecker
     */
    constructor(treeCollisionChecker) {
        this.treeCollisionChecker = treeCollisionChecker;
    }

    /**
     * @param {AbstractItem[]} items
     * @return {boolean}
     */
    check(items) {
        let overlapGroups = [];

        this._flattenGroups(items)
            .forEach((item) => {
                const matchingGroups = this._findMatchingOverlapGroups(overlapGroups, item);
                const newGroup = this._combineOverlappingGroups(matchingGroups, item);
                overlapGroups = this._replaceOverlappingGroups(overlapGroups, matchingGroups, [newGroup]);
            });

        return overlapGroups.length === 1;
    }

    /**
     * @param {AbstractItem[]} items
     * @return {AbstractItem[]}
     * @private
     */
    _flattenGroups(items) {
        return items.reduce(
            (arr, item) => arr.concat(item instanceof AbstractGroupItem ? item.resolveGroupItems() : [item]),
            [],
        );
    }

    /**
     * Find all overlapping groups which has an item that is overlapped by the given item
     *
     * @param {Array.<AbstractItem[]>} overlapGroups
     * @param {AbstractItem} item
     * @return {Array.<AbstractItem[]>}
     * @private
     */
    _findMatchingOverlapGroups(overlapGroups, item) {
        return overlapGroups.filter(group => this._isItemOverlappingGroup(item, group));
    }

    /**
     * Check if the given items overlaps any of the items in the overlapping group
     * @param {AbstractItem} item
     * @param {AbstractItem[]} overlapGroup
     * @return {boolean}
     * @private
     */
    _isItemOverlappingGroup(item, overlapGroup) {
        const bagTreesInGroup = overlapGroup.map(subItem => subItem.getBagTree());

        return this.treeCollisionChecker.check(item.getBagTree(), bagTreesInGroup).length > 0;
    }

    /**
     * Combine multiple groups into one new group
     *
     * @param {Array.<AbstractItem[]>} overlappingGroups
     * @param {AbstractItem} item
     * @return {AbstractItem[]}
     * @private
     */
    _combineOverlappingGroups(overlappingGroups, item) {
        return [].concat(...overlappingGroups, item);
    }

    /**
     * Remove the old groups and add the new groups
     *
     * @param {Array.<AbstractItem[]>} overlappingGroups
     * @param {Array.<AbstractItem[]>} oldGroups
     * @param {Array.<AbstractItem[]>} newGroups
     * @return {Array.<AbstractItem[]>}
     * @private
     */
    _replaceOverlappingGroups(overlappingGroups, oldGroups, newGroups) {
        return overlappingGroups
            .filter(group => oldGroups.indexOf(group) < 0)
            .concat(newGroups);
    }
}
